// Core
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Libraries
import { Trans } from 'react-i18next';
import { useTheme } from 'styled-components';

// Types
import { Theme } from '../../../types/theme';

// Components
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
} from '../Paragraph';
import { ThresholdConfigError } from '../../icons';

// Component
import {
  ErrorIndicatorWrapper,
  TooltipContentBox,
  TooltipPortalContainer,
} from './ThresholdErrorTooltip.style';
import { ThresholdErrorTooltipProps } from './ThresholdErrorTooltip.types';

function ThresholdErrorTooltip({
  errorMessage,
  className,
}: ThresholdErrorTooltipProps): JSX.Element | null {
  // Dependencies
  const theme: Theme = useTheme();
  const iconRef: React.MutableRefObject<
  HTMLDivElement | null
  > = useRef<HTMLDivElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // Don't render anything if there's no error
  if (!errorMessage) {
    return null;
  }

  return (
    <>
      <ErrorIndicatorWrapper
        className={className}
        ref={(el: HTMLDivElement | null) => { iconRef.current = el; }}
        onMouseEnter={() => {
          if (iconRef.current) {
            const rect: DOMRect = iconRef.current.getBoundingClientRect();
            setTooltipPosition({
              top: rect.top,
              left: rect.left + rect.width / 2,
            });
            setTooltipVisible(true);
          }
        }}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <ThresholdConfigError />
      </ErrorIndicatorWrapper>

      {/* Render tooltip via portal when tooltip is visible */}
      {tooltipVisible && createPortal(
        <TooltipPortalContainer
          top={tooltipPosition.top}
          left={tooltipPosition.left}
          visible={tooltipVisible}
        >
          <TooltipContentBox>
            <Paragraph
              align={ParagraphAlign.center}
              color={theme.color.thresholdErrorTooltip.textColor}
              size={ParagraphSize.sm}
            >
              <Trans i18nKey="common.thresholdConfigError" />
            </Paragraph>
          </TooltipContentBox>
        </TooltipPortalContainer>,
        document.body,
      )}
    </>
  );
}

export { ThresholdErrorTooltip };
